import { FC, useState } from 'react';
import { Button, Dropdown, Flex, Switch, Typography } from 'antd';
import { TbChevronDown, TbChevronUp } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { FLOATING_DROPDOWN_OVERLAY_STYLE } from '@/shared/components/dropdowns/DropdownPanel';
import MiddleDot from '@/shared/components/typography/MiddleDot';

import ChangeDetailsPanel from './ChangeDetailsPanel';
import type { PreviewChange } from './changes';

interface ChangeNavigatorProps {
    activeChangeIndex: number;
    changes: PreviewChange[];
    highlightChanges: boolean;
    onHighlightChangesChange: (highlightChanges: boolean) => void;
    onSelectChange: (index: number) => boolean | void;
    resourceLabel?: string;
}

const ChangeNavigator: FC<ChangeNavigatorProps> = ({
    activeChangeIndex,
    changes,
    highlightChanges,
    onHighlightChangesChange,
    onSelectChange,
    resourceLabel = 'Memo',
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const selectChange = (index: number) => {
        setDropdownOpen(onSelectChange(index) === false);
    };

    return (
        <Flex
            align='center'
            gap='0.25rem'
            style={{
                position: 'sticky',
                bottom: '0.25rem',
                zIndex: 2,
                width: 'fit-content',
                margin: '1rem auto 0',
                padding: '0.5rem 0.625rem',
                border: `1px solid ${token.colorBorderSecondary}`,
                borderRadius: token.borderRadiusLG,
                background: token.colorBgElevated,
                boxShadow: token.boxShadowSecondary,
            }}
        >
            <Switch
                size='small'
                checked={highlightChanges}
                aria-label='Highlight changes'
                onChange={onHighlightChangesChange}
            />
            <Typography.Text style={{ marginLeft: '0.25rem' }}>Highlight changes</Typography.Text>
            <span aria-hidden='true'>
                <MiddleDot horizontalMargin='0.25rem' />
            </span>
            <Dropdown
                destroyOnHidden
                onOpenChange={setDropdownOpen}
                open={dropdownOpen}
                overlayStyle={FLOATING_DROPDOWN_OVERLAY_STYLE}
                placement='top'
                popupRender={() => (
                    <ChangeDetailsPanel
                        activeChangeIndex={activeChangeIndex}
                        changes={changes}
                        description={`Select a change to review it in the ${resourceLabel}.`}
                        onSelectChange={selectChange}
                        title='Changes in this version'
                    />
                )}
                trigger={['click']}
            >
                <DropdownButton
                    type='text'
                    size='small'
                    aria-label={`Change ${activeChangeIndex + 1} of ${changes.length}`}
                    onKeyDown={(event) => {
                        if (event.key === 'Escape') {
                            setDropdownOpen(false);
                        }
                    }}
                >
                    {activeChangeIndex + 1} of {changes.length}
                </DropdownButton>
            </Dropdown>
            <span aria-hidden='true'>
                <MiddleDot horizontalMargin='0.25rem' />
            </span>
            <Button
                type='text'
                size='small'
                aria-label='Previous change'
                icon={<TbChevronUp />}
                onClick={() => selectChange(activeChangeIndex - 1)}
            />
            <Button
                type='text'
                size='small'
                aria-label='Next change'
                icon={<TbChevronDown />}
                onClick={() => selectChange(activeChangeIndex + 1)}
            />
        </Flex>
    );
};

export default ChangeNavigator;
